import { AppInventoryOption, type AppInventoryState } from '@openx/types/targeting/appInventory.ts';
import {
  type ComparisonMap,
  ComparisonType,
  Criteria,
  type CriteriaValue,
} from '@openx/types/targeting/targetingValuesTypes.ts';

type DimensionMode = 'allow' | 'block' | 'empty';

const BLOCK_COMPARISON_TYPES: ComparisonType[] = [ComparisonType.DOES_NOT_EQUAL, ComparisonType.DOES_NOT_BEGIN_WITH];

const hasItems = (val: ComparisonMap | undefined, comparisonType: ComparisonType): boolean =>
  Array.isArray(val?.[comparisonType]) && val[comparisonType].length > 0;

/**
 * Classifies a single App Inventory dimension as allow, block or empty.
 * A dimension holding both allow and block items (not reachable through the UI)
 * is treated as block, which resolves to the stricter AND operator.
 */
const getDimensionMode = (val?: ComparisonMap): DimensionMode => {
  if (!val) return 'empty';

  if (BLOCK_COMPARISON_TYPES.some(comparisonType => hasItems(val, comparisonType))) return 'block';

  return Object.values(val).some(items => Array.isArray(items) && items.length > 0) ? 'allow' : 'empty';
};

/**
 * Resolves the operator combining App Name (`appgroup`) with App Bundle ID (`app_bundle_id`).
 *
 * OR when everything configured is in allow mode, AND as soon as either dimension blocks.
 * Inventory lists are allow-only, so they never change the outcome.
 *
 * The API does not derive this value — it only validates it, and rejects the payload when the
 * operator disagrees with the allow/block modes. See `AppGroupValidator.ALLOWED_COMBINATIONS`
 * and `AppInventoryValidator._validate_inter_dimension_operator` in ox-api-core.
 */
export const resolveAppInventoryOperator = (inventoryState: AppInventoryState): CriteriaValue => {
  const appNameMode = getDimensionMode(inventoryState[AppInventoryOption.APP_GROUP]?.val);
  const bundleIdMode = getDimensionMode(inventoryState[AppInventoryOption.APP_BUNDLE_ID]?.val);

  return appNameMode === 'block' || bundleIdMode === 'block' ? Criteria.ALL : Criteria.ANY;
};
